import { Injectable, inject } from '@angular/core';
import { Observable, combineLatest, map } from 'rxjs';

import { FeatureFlags } from '../../api/models/profile/profile-feature-flags.enum';
import { PlanType } from '../../api/models/plans/plan.enum';
import { FeatureFlagQuery } from '../../feature-flag/state/feature-flag.query';
import { Profile } from '../../profile/models/profile.interface';
import { ProfileQuery } from '../../profile/state/profile.query';

/**
 * Who sees AI Posture: a company whose AISPM module is enabled (a pro or enterprise AISPM plan in the profile's
 * module plans, set by the ops plan command), or one shown the prototype embed. There is no AI Posture flag; the CMS
 * nav still names the retired `devAiPosture` on the AI Posture item and as the hide-flag of "AI assets", so both
 * navs resolve that name here instead of in the flag store, where an unfetched flag reads as open.
 */
@Injectable({ providedIn: 'root' })
export class AiPostureAccessService {
    /** The gate name the CMS nav items (and the route's premium dialog) still carry. */
    static readonly CmsGateName = 'devAiPosture';

    private readonly profileQuery = inject(ProfileQuery);
    private readonly featureFlagQuery = inject(FeatureFlagQuery);

    isAispmEnabled(): boolean {
        return AiPostureAccessService.hasAispmModule(this.profileQuery.getValue());
    }

    canView(): boolean {
        return this.isAispmEnabled() || !!this.featureFlagQuery.getFlag(FeatureFlags.DevAiPosturePrototype);
    }

    selectCanView(): Observable<boolean> {
        return combineLatest([
            this.profileQuery.select(profile => AiPostureAccessService.hasAispmModule(profile)),
            this.featureFlagQuery.selectFlag(FeatureFlags.DevAiPosturePrototype),
        ]).pipe(map(([aispmEnabled, prototype]) => aispmEnabled || !!prototype));
    }

    private static hasAispmModule(profile: Profile): boolean {
        const plan = profile?.modulePlanDetails?.AISPM?.plan;
        return plan === PlanType.Pro || plan === PlanType.Enterprise;
    }
}
